import ky from "@/shared/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Data } from "@/shared/types.ts";
import {
  PickupDay,
  PickupDayPatch,
  PickupScheduleConfig,
} from "@/features/pickup-schedule/type.ts";

const SCHEDULE_KEY = "pickup_schedule";
const CONFIG_KEY = "pickup_schedule_config";

export function usePickupSchedule(year: number) {
  return useQuery<Data<PickupDay[]>>({
    queryKey: [SCHEDULE_KEY, year],
    queryFn: (ctx) =>
      ky
        .get<
          Data<PickupDay[]>
        >(`/api/v1/admin/pickup-schedule?year=${year}`, { signal: ctx.signal })
        .json(),
    initialData: { data: [] } as unknown as Data<PickupDay[]>,
  });
}

export function useUpdatePickupDay() {
  const queryClient = useQueryClient();

  return useMutation<
    Data<PickupDay>,
    Error,
    { date: string; patch: PickupDayPatch }
  >({
    mutationFn: ({ date, patch }) =>
      ky
        .put(`/api/v1/admin/pickup-schedule/day/${date}`, { json: patch })
        .json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULE_KEY] });
    },
  });
}

export function useBulkUpdatePickupDays() {
  const queryClient = useQueryClient();

  return useMutation<
    Data<PickupDay[]>,
    Error,
    { dates: string[]; patch: PickupDayPatch }
  >({
    mutationFn: (body) =>
      ky.put(`/api/v1/admin/pickup-schedule/bulk`, { json: body }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULE_KEY] });
    },
  });
}

export function useRegeneratePickupSchedule() {
  const queryClient = useQueryClient();

  return useMutation<Data<boolean>, Error, void>({
    mutationFn: () =>
      ky.post(`/api/v1/admin/pickup-schedule/regenerate`).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULE_KEY] });
    },
  });
}

export function usePickupScheduleConfig() {
  return useQuery<Data<PickupScheduleConfig>>({
    queryKey: [CONFIG_KEY],
    queryFn: (ctx) =>
      ky
        .get<
          Data<PickupScheduleConfig>
        >(`/api/v1/admin/pickup-schedule/config`, { signal: ctx.signal })
        .json(),
  });
}

export function useUpdatePickupScheduleConfig() {
  const queryClient = useQueryClient();

  return useMutation<Data<PickupScheduleConfig>, Error, PickupScheduleConfig>({
    mutationFn: (body) =>
      ky
        .put(`/api/v1/admin/pickup-schedule/config`, { json: body })
        .json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONFIG_KEY] });
    },
  });
}
